'use client';

import {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useMemo,
  useState,
} from 'react';

import { LocationStoreType } from '@/features/listing/listing.types';
import { ProviderLocation } from '@/features/profile/profile.types';
import { getSearchAddressData } from '@/hooks/use-page-props-addressdata';
import { SearchAddressData } from '@/types/SearchAddressData';

type ActiveProviderLocationByDoctorId = {
  [doctorId: string]: string;
};

interface DistanceProviderProps extends PropsWithChildren {
  addressLocation?: SearchAddressData;
}

export type DistanceState = {
  // This is the searched location for searching providers by distance
  location: LocationStoreType;
  setLocation: Dispatch<SetStateAction<LocationStoreType>>;
  // This is to store the active location for profile/booking pages but is being deprecated
  activeDisplayLocation: ProviderLocation | null;
  setActiveDisplayLocation: Dispatch<SetStateAction<ProviderLocation | null>>;
  // This is so we can store all provider locations for showing distance on the UI for profile/booking pages
  providerLocations: ProviderLocation[] | null;
  setProviderLocations: Dispatch<SetStateAction<ProviderLocation[] | null>>;
  // This is used when user changing location for a specific provider on the listing page
  activeProviderId: string;
  setActiveProviderId: Dispatch<SetStateAction<string>>;
  // This is used to map doctorId to their active provider location id for easy lookup
  activeProviderLocationByDoctorId: ActiveProviderLocationByDoctorId;
  setActiveProviderLocationByDoctorId: Dispatch<SetStateAction<ActiveProviderLocationByDoctorId>>;
};

export const DistanceContext = createContext<DistanceState | undefined>(undefined);

export const DistanceProvider = ({
  children,
  addressLocation,
}: DistanceProviderProps): JSX.Element => {
  const searchAddressData: ReturnType<typeof getSearchAddressData> = addressLocation
    ? getSearchAddressData(addressLocation)
    : null;

  const initialState = searchAddressData
    ? searchAddressData
    : {
        zip: '',
        latitude: null,
        longitude: null,
        address: '',
      };

  const [location, setLocation] = useState<LocationStoreType>(initialState);
  const [activeDisplayLocation, setActiveDisplayLocation] = useState<ProviderLocation | null>(null);
  const [providerLocations, setProviderLocations] = useState<ProviderLocation[] | null>(null);

  // add active id
  const [activeProviderId, setActiveProviderId] = useState<string>('');
  const [activeProviderLocationByDoctorId, setActiveProviderLocationByDoctorId] =
    useState<ActiveProviderLocationByDoctorId>({});

  const contextValues = useMemo(
    () => ({
      location,
      setLocation,
      activeDisplayLocation,
      setActiveDisplayLocation,
      providerLocations,
      setProviderLocations,
      activeProviderId,
      setActiveProviderId,
      activeProviderLocationByDoctorId,
      setActiveProviderLocationByDoctorId,
    }),
    [
      location,
      activeDisplayLocation,
      providerLocations,
      activeProviderId,
      activeProviderLocationByDoctorId,
    ]
  );

  return <DistanceContext.Provider value={contextValues}>{children}</DistanceContext.Provider>;
};
